import axios from 'axios';
import API_CONFIG from './config';
import { toPathSegment } from '@/utils/imageUrl';

const api = axios.create(API_CONFIG);

// Accommodations (Hotels)
export const accommodationsService = {
  getAll: (params) => api.get('/accommodations', { params }),
  getById: (id, params) => api.get(`/accommodations/${id}`, { params }),
  search: (params) => api.get('/accommodations/search', { params }),
  // Get hotels that have agreements in acc_agreements table
  // Use { featured: 1 } to get only featured hotels
  getWithAgreements: (params) => api.get('/accommodations/with-agreements', { params }),
};

// Languages
export const languagesService = {
  getAll: (params) => api.get('/languages', { params }),
};

// Site partner: network / southcrete.com – schedules, prices and commissions shown on the public site use this partner.
const SITE_PARTNER_PARAMS = { for_site: 1 };

// Excursions
export const excursionsService = {
  getAll: (params) => api.get('/excursions', { params: { ...SITE_PARTNER_PARAMS, ...params } }),
  getById: (id) => api.get(`/excursions/${id}`, { params: SITE_PARTNER_PARAMS }),
  getBySlug: (slug) => api.get(`/excursions/${encodeURIComponent(toPathSegment(slug, ''))}`, { params: SITE_PARTNER_PARAMS }),
  search: (params) => api.get('/excursions/search', { params: { ...SITE_PARTNER_PARAMS, ...params } }),
  getSchedules: (excursionId) => api.get(`/excursions/${excursionId}/schedules`, { params: SITE_PARTNER_PARAMS }),
  getPickups: (excursionId) => api.get(`/excursions/${excursionId}/pickups`, { params: SITE_PARTNER_PARAMS }),
  getAllPickups: () => api.get('/excursions/pickups', { params: SITE_PARTNER_PARAMS }),
  getSchedulePickups: (scheduleId) =>
    api.get('/excursions/pickups', { params: { schedule_id: scheduleId, ...SITE_PARTNER_PARAMS } }),
  getPricelists: (year, excId = null, regionId = null) =>
    api.get('/excursions/pricelists', {
      params: { year, ...(excId != null && excId !== '' ? { exc_id: excId } : {}), ...(regionId != null && regionId !== '' ? { region_id: regionId } : {}), ...SITE_PARTNER_PARAMS },
    }),
  getSalesCapacity: (params = {}) =>
    api.get('/excursions/sales-capacity', { params: { ...SITE_PARTNER_PARAMS, ...params } }),
  getPartnerPricelists: (year, excId = null, partnerId = null, regionId = null) =>
    api.get('/excursions/pricelists', {
      params: {
        year,
        ...(excId != null && excId !== '' ? { exc_id: excId } : {}),
        ...(partnerId != null && partnerId !== '' ? { partner_id: partnerId } : {}),
        ...(regionId != null && regionId !== '' ? { region_id: regionId } : {}),
      },
    }),
  createBooking: (payload, attribution = {}) => {
    const params = new URLSearchParams({ for_site: '1' });
    if (attribution?.tracking_code) {
      params.set('tracking_code', attribution.tracking_code);
    }
    return api.post(`/excursions/bookings?${params.toString()}`, payload);
  },
  createBookingCheckoutSession: (bookingId, payload = {}) =>
    api.post('/excursions/bookings/checkout-session', { booking_id: bookingId, ...payload }),
};

// Transfers
export const transfersService = {
  getAll: (params) => api.get('/transfers', { params }),
  getById: (id) => api.get(`/transfers/${id}`),
  search: (params) => api.get('/transfers/search', { params }),
};

/** Public car rental catalogue & locations (no auth; used by marketing site) */
export const carRentalService = {
  /** Public fleet catalogue. Supports { featured: 1 } for homepage cards. */
  getCatalog: (params = {}) => api.get('/cars/site/catalog', { params }),
  /** Fleet header categories (Economy, SUV, …) with price group ids — from manager Car extras → Fleet categories */
  getDisplayCategories: () => api.get('/cars/site/display-categories'),
  getLocations: (params = {}) => api.get('/cars/site/locations', { params }),
  getModelBySlug: (slug) => api.get(`/cars/site/model/${encodeURIComponent(toPathSegment(slug, ''))}`),
  /** Pricelist quote: group_id, delivery_date, collection_date (Y-m-d); optional extra_quantities (JSON of rate id -> qty), legacy extra_ids; insurance_ids */
  getQuote: (params) => api.get('/cars/site/quote', { params }),
  /** Batch catalog quotes: group_ids=1,2,3 plus the same date/time/ref params as getQuote */
  getQuotes: (params) => api.get('/cars/site/quotes', { params }),
  /** Remaining free cars by model_id for a date/time window (no plates). */
  getAvailability: (params) => api.get('/cars/site/availability', { params }),
  getInsurances: (params) => api.get('/cars/site/insurances', { params }),
  getExtras: (params) => api.get('/cars/site/extras', { params }),
  /** Rental T&C HTML for fleet quick-view (manager Car Extras → Rental terms). */
  getRentalTerms: () => api.get('/cars/site/rental-terms'),
  createBooking: (payload, attribution = {}) => {
    const params = new URLSearchParams({ for_site: '1' });
    if (attribution?.tracking_code) {
      params.set('tracking_code', attribution.tracking_code);
    }
    return api.post(`/cars/site/rentals?${params.toString()}`, payload);
  },
  createBookingCheckoutSession: (rentalId, payload = {}) =>
    api.post('/cars/site/rentals/checkout-session?for_site=1', { rental_id: rentalId, ...payload }),
};

// Partners (Portal)
// Legacy compatibility export: portal auth lives under `/partner-auth/*`.
export const partnersService = {
  getAll: () => api.get('/partners'),
  getById: (id) => api.get(`/partners/${id}`),
  login: (credentials) => api.post('/partner-auth/login', credentials),
};

const withPartnerToken = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const partnerPortalAuthService = {
  login: (credentials) => api.post('/partner-auth/login', credentials),
  me: (token) => api.get('/partner-auth/me', withPartnerToken(token)),
  resolvePartnerSlug: (partnerSlug) =>
    api.get(`/partner-auth/resolve/${encodeURIComponent(toPathSegment(partnerSlug, ''))}`),
  forgotPassword: (payload) => api.post('/partner-auth/forgot-password', payload),
  resetPassword: (payload) => api.post('/partner-auth/reset-password', payload),
};

export const hotelEngineService = {
  getStatus: (params = {}) => api.get('/hotel-engine/status', { params }),
  getDestinations: (params = {}) => api.get('/hotel-engine/destinations', { params }),
  search: (params = {}) => api.get('/hotel-engine/search', { params }),
  quote: (payload) => api.post('/hotel-engine/quote', payload),
  book: (payload) => api.post('/hotel-engine/book', payload),
  createCheckoutSession: (bookingId, payload = {}) =>
    api.post('/hotel-engine/bookings/checkout-session', { booking_id: bookingId, ...payload }),
};

export const partnerPortalService = {
  getExcursions: (token, params = {}) =>
    api.get('/excursions', { ...withPartnerToken(token), params }),
  getExcursionSalesCapacity: (token, params = {}) =>
    api.get('/excursions/sales-capacity', { ...withPartnerToken(token), params }),
  getDefaults: (token, params = {}) =>
    api.get('/partner-portal/defaults', { ...withPartnerToken(token), params }),
  getExcursionBookings: (token, params = {}) =>
    api.get('/excursions/bookings', { ...withPartnerToken(token), params }),
  createExcursionBooking: (token, payload) =>
    api.post('/excursions/bookings', payload, withPartnerToken(token)),
  getCarRentals: (token, params = {}) =>
    api.get('/cars/rentals', { ...withPartnerToken(token), params }),
  createCarRental: (token, payload) =>
    api.post('/cars/rentals', payload, withPartnerToken(token)),
  searchHotels: (token, params = {}) =>
    api.get('/hotel-engine/search', { ...withPartnerToken(token), params }),
  getHotelDestinations: (token) =>
    api.get('/hotel-engine/destinations', withPartnerToken(token)),
  quoteHotel: (token, payload) =>
    api.post('/hotel-engine/quote', payload, withPartnerToken(token)),
  bookHotel: (token, payload) =>
    api.post('/hotel-engine/book', payload, withPartnerToken(token)),
  getHotelBookings: (token, params = {}) =>
    api.get('/hotel-engine/bookings', { ...withPartnerToken(token), params }),
  cancelHotelBooking: (token, id) =>
    api.post(`/hotel-engine/bookings/${id}/cancel`, {}, withPartnerToken(token)),
};

export const partnerPortalReportsService = {
  getOverview: (token, params = {}) =>
    api.get('/partner-portal/reports/overview', { ...withPartnerToken(token), params }),
  exportCsv: (token, params = {}) =>
    api.get('/partner-portal/reports/export', {
      ...withPartnerToken(token),
      params,
      responseType: 'blob',
    }),
};

// Destinations
export const destinationsService = {
  getAll: (regionId = null) => {
    const url = regionId ? `/destinations?for_site=1&region_id=${regionId}` : '/destinations?for_site=1';
    return api.get(url);
  },
  getPlaces: (destinationId) => api.get(`/destinations/${destinationId}/places`, { params: { for_site: 1 } }),
};

// Regions
export const regionsService = {
  getAll: (params = {}) => api.get('/regions', { params: { for_site: 1, ...params } }),
  getById: (id) => api.get(`/regions/${id}`),
  /** Public single region by URL slug (active + online regions only); includes highlights, image_url, gallery_urls */
  getBySlug: (slug) => api.get(`/regions/by-slug/${encodeURIComponent(toPathSegment(slug, ''))}`),
  getDestinations: (regionId) => api.get(`/regions/${regionId}/destinations`, { params: { for_site: 1 } }),
  getPlaces: (regionId) => api.get(`/regions/${regionId}/places`, { params: { for_site: 1 } }),
  getHighlights: (regionId) => api.get(`/regions/${regionId}/highlights`, { params: { for_site: 1 } }),
};

// Seasons
export const seasonsService = {
  getAll: () => api.get('/seasons'),
};

// Services Page
export const servicesPageService = {
  getAllSections: () => api.get('/services-page'),
  getSectionByKey: (key) => api.get(`/services-page/section-by-key/${key}`),
};

// About Page
export const aboutPageService = {
  getAllSections: () => api.get('/about-page'),
  getSectionByKey: (key) => api.get(`/about-page/section-by-key/${key}`),
};

// Home Page
export const homePageService = {
  getAllSections: () => api.get('/home-page'),
  getSectionByKey: (key) => api.get(`/home-page/section-by-key/${key}`),
};

// East Crete Page
export const eastCretePageService = {
  getAllSections: () => api.get('/east-crete-page', { 
    // Add cache-busting timestamp to prevent stale data
    params: { _t: Date.now() }
  }),
  getSectionByKey: (key) => api.get(`/east-crete-page/section-by-key/${key}`, {
    // Add cache-busting timestamp to prevent stale data
    params: { _t: Date.now() }
  }),
};

// Book Page
export const bookPageService = {
  getAllSections: () => api.get('/book-page'),
  getSectionByKey: (key) => api.get(`/book-page/section-by-key/${encodeURIComponent(key)}`),
  getSectionFlagsByKey: (key) => api.get(`/book-page/section-flags/${encodeURIComponent(key)}`),
};

// Contact Page
export const contactPageService = {
  getAllSections: () => api.get('/contact-page'),
  getSectionByKey: (key) => api.get(`/contact-page/section-by-key/${key}`),
};

export const inquiriesService = {
  submit: (payload) => api.post('/inquiries', payload),
};

// Site Builder Globals (styles, scripts, fonts, footer, integrations)
export const siteGlobalsService = {
  get: () => api.get('/site-globals'),
};

export default api;

